import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isThemeMode, isThemePalette, type ThemeMode, type ThemePalette } from '@wms/shared';
import { api } from './api.js';
import { useAuth } from './auth-context.js';
import {
  type Appearance,
  applyAppearance,
  prefersDarkQuery,
  readAppearance,
  resolveMode,
} from './appearance.js';

interface AppearanceContextValue extends Appearance {
  /** The mode actually painted right now — 'system' already resolved. */
  resolvedTheme: 'light' | 'dark';
  /** True while a save is in flight; the picker disables itself to avoid races. */
  saving: boolean;
  setAppearance: (patch: Partial<Appearance>) => Promise<void>;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [appearance, setLocal] = useState<Appearance>(readAppearance);
  const [resolvedTheme, setResolved] = useState<'light' | 'dark'>(() =>
    resolveMode(readAppearance().themeMode),
  );
  const [saving, setSaving] = useState(false);
  // Monotonic sequence over saves: only the newest may commit its result or
  // roll back. Without it, two quick picks can land out of order and leave the
  // painted theme disagreeing with what the account actually stores.
  const saveSeq = useRef(0);

  // Adopt the account-level preference once the session resolves. Only fields
  // the server actually carries win: a null column means this account never
  // explicitly chose, so the device-local pick stands rather than being reset.
  const serverPalette = user?.themePalette ?? null;
  const serverMode = user?.themeMode ?? null;
  useEffect(() => {
    if (!isThemePalette(serverPalette) && !isThemeMode(serverMode)) return;
    setLocal((current) => {
      const next: Appearance = {
        themePalette: isThemePalette(serverPalette) ? serverPalette : current.themePalette,
        themeMode: isThemeMode(serverMode) ? serverMode : current.themeMode,
      };
      setResolved(applyAppearance(next));
      return next;
    });
  }, [serverPalette, serverMode]);

  // In 'system' mode, follow the OS. Re-resolving on every change also keeps
  // `resolvedTheme` honest for anything rendering off it.
  useEffect(() => {
    const media = prefersDarkQuery();
    if (!media) return;
    const sync = () => setResolved(applyAppearance(appearance));
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [appearance]);

  const setAppearance = useCallback(
    async (patch: Partial<Appearance>) => {
      const seq = ++saveSeq.current;
      const previous = appearance;
      const next: Appearance = { ...appearance, ...patch };

      // Paint first — a theme pick should feel instant even on a slow link.
      setLocal(next);
      setResolved(applyAppearance(next));

      if (!user) return; // Anonymous (login screen): device-local only.

      setSaving(true);
      try {
        await api<unknown>('/settings/appearance', { method: 'PUT', body: { ...next } });
      } catch (err) {
        if (seq === saveSeq.current) {
          setLocal(previous);
          setResolved(applyAppearance(previous));
        }
        throw err;
      } finally {
        if (seq === saveSeq.current) setSaving(false);
      }
    },
    [appearance, user],
  );

  const value = useMemo<AppearanceContextValue>(
    () => ({ ...appearance, resolvedTheme, saving, setAppearance }),
    [appearance, resolvedTheme, saving, setAppearance],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error('useAppearance must be used inside <AppearanceProvider>');
  return ctx;
}

export type { ThemeMode, ThemePalette };
